import { createContext, useContext } from "react"

import type { CampaignWizardDraft } from "../campaigns/wizard/campaign-drafts.ts"
import type { CampaignAudience } from "../types/campaigns.types.ts"
import type {
  Channel,
  ContactRef,
  RecipientRef,
  RecordRef
} from "../types/compose.types.ts"

export type SettingsSection = "general" | "email" | "sms" | "rules" | "usage"

export const SETTINGS_SECTIONS: SettingsSection[] = [
  "general",
  "email",
  "sms",
  "rules",
  "usage"
]

export interface ComposerRecipient extends RecipientRef {
  name: string
}

export interface ComposerPrefill {
  channel?: Channel
  record?: RecordRef & { label?: string }
  recipients?: ComposerRecipient[]
}

export interface CampaignPrefill {
  audience?: CampaignAudience
  ids?: string[]
  channel?: Channel
  audienceLabel?: string
  filtersUsed?: string[]
  draft?: CampaignWizardDraft
}

export interface HubActions {
  openComposer: (prefill?: ComposerPrefill) => void
  openCampaignWizard: (prefill?: CampaignPrefill) => void
  openSettings: (section?: SettingsSection) => void
  openContactPreferences: (contact?: ContactRef) => void
  openSetup: () => void
}

const noop = () => undefined

const HubContext = createContext<HubActions>({
  openComposer: noop,
  openCampaignWizard: noop,
  openSettings: noop,
  openContactPreferences: noop,
  openSetup: noop
})

export const HubActionsProvider = HubContext.Provider

export const useHub = () => useContext(HubContext)

export const isSettingsSection = (
  value: string | null
): value is SettingsSection =>
  !!value && (SETTINGS_SECTIONS as string[]).includes(value)

export const HUB_PARAMS = {
  settings: "settings",
  preferences: "preferences",
  contact: "contact",
  setup: "setup"
} as const
